import * as Sentry from '@sentry/react';
import { sonner } from '@sr/ui-library';
import { UseQueryResult } from '@tanstack/react-query';
import React, { ReactNode } from 'react';
import { convertStringToSentryTag } from '../../utils/helpers/common';
import { ErrorFallback } from './ErrorFallback';
import { LoadingFallback } from './LoadingFallback';

const { toast } = sonner;

// Multiple queries props
interface MultipleQueriesProps<T extends Record<string, UseQueryResult>> {
  children:
    | ((data: {
        [K in keyof T]: NonNullable<T[K]['data']>;
      }) => ReactNode)
    | ReactNode;
  errorFallback?: <QUERIES = T>(queriesWithErrors: QUERIES) => ReactNode;
  loadingFallback?: ReactNode;
  notFetched?: ReactNode;
  queries: T;
}

/**
 * MultipleQueryResults accepts a record of query results objects from React Query
 * and invokes its children with the resolved data when available. Optional props
 * allow for supplying custom UI elements for error, loading, or not-fetched states.
 *
 * **How is it different from SingleQueryResults?:** Loading and error states are
 * shared between all queries. If one query has an error or is loading, all queries will
 * show the same error or loading state.
 *
 * Queries' data is type-safe and guaranteed to be defined. Example:
 * ```tsx
 * <MultipleQueryResults queries={{
 *    workspacesData: workspacesQuery,
 *    titlesData: titlesQuery,
 *  }}
 * >
 *   {({ workspacesData, titlesData }) => (
 *     <div>
 *       {workspacesData.prop}
 *       {titlesData.prop}
 *     </div>
 *   )}
 * </MultipleQueryResults>
 * ```
 */
export const MultipleQueryResults = <T extends Record<string, UseQueryResult>>(
  props: MultipleQueriesProps<T>,
) => {
  const { children, errorFallback, loadingFallback, notFetched, queries } =
    props;

  // Derived values
  const queriesObjectEntries = Object.entries(queries);
  const queriesWithErrorsObjectEntries = queriesObjectEntries.filter(
    ([, query]) => query.status === 'error',
  );

  // Booleans
  const areAnyQueriesLoading = Object.values(queries).some(
    (query) => query.status === 'pending' && query.fetchStatus === 'fetching',
  );
  const atLeastOneQueryWithErrorHasNoData = queriesWithErrorsObjectEntries.some(
    ([, query]) => query.data === undefined || query.data === null,
  );
  const hasErrors = queriesWithErrorsObjectEntries.length > 0;
  const hasDataForAllQueries = queriesObjectEntries.every(
    ([, query]) => query.data !== undefined && query.data !== null,
  );
  /**
   * At least one query has never started (e.g. `enabled: false` or a
   * `skipToken` queryFn, as `useActiveWorkspace` uses before a workspaceId is
   * known): `status: 'pending'`, `fetchStatus: 'idle'`, no data. This is
   * distinct from `areAnyQueriesLoading` (pending AND fetching) and from the
   * final "no data returned" branch below — that branch is for a query that
   * DID run and still came back empty, which is a real error. Conflating the
   * two made a query that never started log a misleading Sentry error and
   * render null instead of a not-fetched state.
   */
  const isAnyQueryNotFetched = queriesObjectEntries.some(
    ([, query]) =>
      (query.data === undefined || query.data === null) &&
      query.status === 'pending' &&
      query.fetchStatus !== 'fetching',
  );

  /**
   * Log error(s) to Sentry
   */
  if (hasErrors) {
    Sentry.captureException(
      new Error(`Error(s) in <${MultipleQueryResults.name} />`),
      {
        contexts: {
          tanstackQuery: {
            /**
             * Return an object of each query's name (key)
             * from props and its error object.
             */
            errors: Object.fromEntries(queriesWithErrorsObjectEntries),
          },
        },
        tags: {
          type: convertStringToSentryTag('MultipleQueryResults'),
        },
      },
    );
  }

  /**
   * This only shows an error if the data for a query is undefined.
   * If there is data in the cache, then a previous query likely
   * succeeded and the error is due to a refetch.
   *
   * In this case we can log the error (above),
   * but avoid disrupting the UI.
   */
  if (hasErrors && atLeastOneQueryWithErrorHasNoData) {
    toast.error('An error occurred', {
      description: queriesWithErrorsObjectEntries.map(([key, query], index) => (
        <React.Fragment key={key}>
          {query.error?.message}
          {index < queriesWithErrorsObjectEntries.length - 1 && <br />}
        </React.Fragment>
      )),
    });

    return errorFallback ? (
      errorFallback(Object.fromEntries(queriesWithErrorsObjectEntries))
    ) : (
      <ErrorFallback
        error={queriesWithErrorsObjectEntries.map(
          ([key, query]) => query.error,
        )}
      />
    );
  }

  /**
   * Are any queries loading for the first time?
   */
  if (areAnyQueriesLoading) {
    return loadingFallback ?? <LoadingFallback />;
  }

  if (hasDataForAllQueries) {
    /**
     * `children` is typed (and documented above) as receiving each query's
     * resolved `data`, keyed the same as `queries` — not the query result
     * objects themselves. Map to `.data` here rather than passing `queries`
     * straight through.
     */
    const dataByKey = Object.fromEntries(
      queriesObjectEntries.map(([key, query]) => [key, query.data]),
    ) as { [K in keyof T]: NonNullable<T[K]['data']> };

    return typeof children === 'function' ? children(dataByKey) : children;
  }

  /**
   * A query that hasn't fetched yet (and has no initial data) isn't an
   * error — it just hasn't started. See the comment on
   * `isAnyQueryNotFetched` above for why this must be checked before the
   * Sentry capture below.
   */
  if (isAnyQueryNotFetched) {
    return notFetched ?? null;
  }

  Sentry.captureException(new Error('No data returned from API'), {
    tags: {
      type: convertStringToSentryTag('MultipleQueryResults'),
    },
  });

  return null;
};
